// `common/directives/switch/switch.tpl.html` — the app-wide on/off switch.
//
// In `common/` rather than `shortlist/` because upstream puts it there: it is a shared
// directive the whole app uses, and the shortlist's automation toggle is only the first caller
// this port needs.
//
// ⚠️ THE ANIMATION CLASSES ARE NOT PORTED, AND THAT IS PIXEL-SAFE. Upstream toggles
// `on-animate` / `off-animate` for the duration of the slide; the capture kills animations
// before every screenshot (`KILL_ANIMATIONS`), so a reference never contains a mid-slide
// state, and reproducing the classes would add machinery the diff cannot see. `on` / `off` and
// `disabled`, which are steady states, are ported.
import { createElement, type ReactElement, type ReactNode } from 'react'

export interface WooSwitchProps {
  isOn: boolean
  label?: string
  disabled?: boolean
  onToggle?: () => void
  /**
   * The caller's class, which goes on the `<woo-switch>` ELEMENT and not on the wrapper.
   *
   * ⚠️ THE HOST ELEMENT IS LOAD-BEARING, exactly as it is for the page components (see
   * PageHost). Callers style this by nesting UNDER their own class:
   *
   *     .hunter-automation-switch { .switch-wrapper { flex-direction: row-reverse; … } }
   *
   * so putting that class on the wrapper instead collapses parent and child into one node and
   * the rule matches nothing. For the automation banner that means losing
   * `row-reverse` — the label would render on the wrong side of the switch, which looks
   * deliberate and is not.
   */
  className?: string
  /** `ng-transclude` — the content inside the switch body, distinct from the label beside it. */
  children?: ReactNode
  /**
   * `label-html` — the SAME slot as `label`, in a second `<span class="switch-label">` that
   * upstream renders only when the string form is absent. The preferences form is its only
   * caller and passes two lines of markup; transcribed as nodes rather than as an HTML string
   * so nothing in this package needs `dangerouslySetInnerHTML`.
   */
  labelNode?: ReactNode
}

export function WooSwitch({
  isOn,
  label,
  disabled = false,
  onToggle,
  className,
  children,
  labelNode,
}: WooSwitchProps): ReactElement {
  return createElement(
    'woo-switch',
    className === undefined ? null : { class: className },
    <div
      className={['switch-wrapper', isOn ? 'on' : 'off', disabled ? 'disabled' : ''].filter(Boolean).join(' ')}
      role="switch"
      aria-checked={isOn}
      aria-label={label}
      tabIndex={disabled ? -1 : 0}
      onClick={() => {
        if (!disabled) onToggle?.()
      }}
      onKeyDown={(event) => {
        if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return
        event.preventDefault()
        onToggle?.()
      }}
    >
      <div className="switch-container">
        <span className="switch-inner-text">{children}</span>
        <span className="switcher" />
      </div>
      {label === undefined || label.length === 0 ? null : <span className="switch-label">{label}</span>}
      {labelNode === undefined ? null : <span className="switch-label">{labelNode}</span>}
    </div>,
  )
}
